import React, { useEffect, useRef, useState } from 'react';
import { clsx } from 'clsx';
import { jsxs, jsx } from 'react/jsx-runtime';
import { createPortal } from 'react-dom';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { HelpCircle } from 'lucide-react';

// src/components/Button.tsx
function cn(...inputs) {
  return clsx(inputs);
}
var sizeClasses = {
  xs: "px-2 py-1 text-xs gap-1",
  sm: "px-3 py-1.5 text-sm gap-1.5",
  md: "px-4 py-2 text-sm gap-2",
  lg: "px-6 py-3 text-base gap-2"
};
var variantClasses = {
  primary: "bg-tg-primary hover:bg-tg-primary-hover active:bg-tg-primary-active text-white border-transparent",
  secondary: "bg-tg-bg-secondary hover:bg-tg-bg-tertiary text-tg-text border-tg-border",
  success: "bg-tg-success hover:bg-tg-success/90 text-white border-transparent",
  danger: "bg-tg-error hover:bg-tg-error/90 text-white border-transparent",
  ghost: "bg-transparent hover:bg-tg-bg-secondary text-tg-text border-transparent",
  "ghost-sm": "bg-transparent hover:bg-gray-100 text-gray-600 hover:text-gray-800 border-transparent text-xs px-2 py-1"
};
var Button = React.forwardRef(
  ({
    variant = "primary",
    size = "md",
    fullWidth = false,
    loading = false,
    icon,
    children,
    className,
    disabled,
    ...props
  }, ref) => {
    return /* @__PURE__ */ jsxs(
      "button",
      {
        ref,
        className: cn(
          // Base styles
          "inline-flex items-center justify-center",
          "rounded-lg font-medium",
          "transition-all duration-200",
          "disabled:opacity-50 disabled:cursor-not-allowed",
          "active:scale-95",
          "border",
          // Size
          sizeClasses[size],
          // Variant
          variantClasses[variant],
          // Full width
          fullWidth && "w-full",
          className
        ),
        disabled: disabled || loading,
        ...props,
        children: [
          loading && /* @__PURE__ */ jsxs(
            "svg",
            {
              className: "animate-spin h-4 w-4",
              xmlns: "http://www.w3.org/2000/svg",
              fill: "none",
              viewBox: "0 0 24 24",
              children: [
                /* @__PURE__ */ jsx(
                  "circle",
                  {
                    className: "opacity-25",
                    cx: "12",
                    cy: "12",
                    r: "10",
                    stroke: "currentColor",
                    strokeWidth: "4"
                  }
                ),
                /* @__PURE__ */ jsx(
                  "path",
                  {
                    className: "opacity-75",
                    fill: "currentColor",
                    d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  }
                )
              ]
            }
          ),
          icon && !loading && /* @__PURE__ */ jsx("span", { className: "flex items-center", children: icon }),
          children
        ]
      }
    );
  }
);
Button.displayName = "Button";
var Input = React.forwardRef(
  ({ label, error, helperText, icon, className, ...props }, ref) => {
    return /* @__PURE__ */ jsxs("div", { className: "w-full", children: [
      label && /* @__PURE__ */ jsxs("label", { className: "block text-sm font-medium text-text-primary mb-1.5", children: [
        label,
        props.required && /* @__PURE__ */ jsx("span", { className: "text-tg-danger ml-1", children: "*" })
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "relative", children: [
        icon && /* @__PURE__ */ jsx("div", { className: "absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary pointer-events-none", children: icon }),
        /* @__PURE__ */ jsx(
          "input",
          {
            ref,
            className: cn(
              "input",
              error && "error",
              icon && "pl-10",
              className
            ),
            ...props
          }
        )
      ] }),
      error && /* @__PURE__ */ jsx("p", { className: "mt-1.5 text-sm text-tg-danger", children: error }),
      helperText && !error && /* @__PURE__ */ jsx("p", { className: "mt-1.5 text-sm text-text-secondary", children: helperText })
    ] });
  }
);
Input.displayName = "Input";
var TextArea = ({
  label,
  error,
  helperText,
  icon,
  className = "",
  ...props
}) => {
  return /* @__PURE__ */ jsxs("div", { className: "w-full", children: [
    label && /* @__PURE__ */ jsx("label", { className: "block text-sm font-medium text-tg-text mb-2", children: label }),
    /* @__PURE__ */ jsxs("div", { className: "relative", children: [
      icon && /* @__PURE__ */ jsx("div", { className: "absolute left-3 top-3 text-tg-hint", children: icon }),
      /* @__PURE__ */ jsx(
        "textarea",
        {
          className: `
            w-full px-4 py-3 min-h-[100px]
            bg-tg-bg-secondary text-tg-text placeholder-tg-hint
            border border-tg-border rounded-xl
            focus:outline-none focus:ring-2 focus:ring-tg-primary focus:border-transparent
            transition-all duration-200
            resize-vertical
            ${icon ? "pl-10" : ""}
            ${error ? "border-red-500 focus:ring-red-500" : ""}
            ${className}
          `.trim().replace(/\s+/g, " "),
          ...props
        }
      )
    ] }),
    error && /* @__PURE__ */ jsx("p", { className: "mt-2 text-sm text-red-500", children: error }),
    helperText && !error && /* @__PURE__ */ jsx("p", { className: "mt-2 text-sm text-tg-hint", children: helperText })
  ] });
};
var Select = React.forwardRef(
  ({ label, error, helperText, options, placeholder, className, ...props }, ref) => {
    return /* @__PURE__ */ jsxs("div", { className: "w-full", children: [
      label && /* @__PURE__ */ jsxs("label", { className: "block text-sm font-medium text-tg-text mb-1.5", children: [
        label,
        props.required && /* @__PURE__ */ jsx("span", { className: "text-tg-error ml-1", children: "*" })
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "relative", children: [
        /* @__PURE__ */ jsxs(
          "select",
          {
            ref,
            className: cn(
              "input appearance-none pr-10 cursor-pointer",
              error && "error",
              !props.value && placeholder && "text-tg-hint",
              className
            ),
            ...props,
            children: [
              placeholder && /* @__PURE__ */ jsx("option", { value: "", disabled: true, hidden: true, children: placeholder }),
              options.map((option) => /* @__PURE__ */ jsx(
                "option",
                {
                  value: option.value,
                  disabled: option.disabled,
                  className: "text-tg-text",
                  children: option.label
                },
                option.value
              ))
            ]
          }
        ),
        /* @__PURE__ */ jsx("div", { className: "absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-tg-hint", children: /* @__PURE__ */ jsx("svg", { width: "16", height: "16", viewBox: "0 0 16 16", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: /* @__PURE__ */ jsx("path", { d: "M4 6L8 10L12 6", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" }) }) })
      ] }),
      error && /* @__PURE__ */ jsx("p", { className: "mt-1 text-xs text-tg-error", children: error }),
      helperText && !error && /* @__PURE__ */ jsx("p", { className: "mt-1 text-xs text-tg-hint", children: helperText })
    ] });
  }
);
Select.displayName = "Select";
var Checkbox = ({
  label,
  helperText,
  className = "",
  ...props
}) => {
  return /* @__PURE__ */ jsxs("label", { className: "flex items-start gap-3 cursor-pointer group", children: [
    /* @__PURE__ */ jsx("div", { className: "relative flex items-center justify-center", children: /* @__PURE__ */ jsx(
      "input",
      {
        type: "checkbox",
        className: `
            w-5 h-5 rounded border-2 border-tg-border
            bg-tg-bg-secondary text-tg-primary
            focus:outline-none focus:ring-2 focus:ring-tg-primary focus:ring-offset-2
            transition-all duration-200
            cursor-pointer
            checked:bg-tg-primary checked:border-tg-primary
            ${className}
          `.trim().replace(/\s+/g, " "),
        ...props
      }
    ) }),
    (label || helperText) && /* @__PURE__ */ jsxs("div", { className: "flex-1", children: [
      label && /* @__PURE__ */ jsx("div", { className: "text-sm font-medium text-tg-text group-hover:text-tg-primary transition-colors", children: label }),
      helperText && /* @__PURE__ */ jsx("div", { className: "text-xs text-tg-hint mt-1", children: helperText })
    ] })
  ] });
};
var Card = React.forwardRef(
  ({ hoverable = false, children, className, ...props }, ref) => {
    return /* @__PURE__ */ jsx(
      "div",
      {
        ref,
        className: cn(
          "card",
          hoverable && "card-hoverable",
          className
        ),
        ...props,
        children
      }
    );
  }
);
Card.displayName = "Card";
var CardHeader = ({
  title,
  subtitle,
  action,
  className = ""
}) => {
  return /* @__PURE__ */ jsxs("div", { className: cn("p-6 pb-4 flex items-start justify-between", className), children: [
    /* @__PURE__ */ jsxs("div", { className: "flex-1 min-w-0", children: [
      /* @__PURE__ */ jsx("h3", { className: "text-lg font-semibold text-tg-text truncate", children: title }),
      subtitle && /* @__PURE__ */ jsx("p", { className: "text-sm text-tg-hint mt-1", children: subtitle })
    ] }),
    action && /* @__PURE__ */ jsx("div", { className: "flex-shrink-0 ml-4", children: action })
  ] });
};
var CardBody = ({ children, className = "" }) => {
  return /* @__PURE__ */ jsx("div", { className: cn("px-6 py-4", className), children });
};
var CardFooter = ({ children, className = "" }) => {
  return /* @__PURE__ */ jsx("div", { className: cn("px-6 py-4 pt-4 border-t border-gray-100", className), children });
};
var Badge = React.forwardRef(
  ({ variant = "neutral", children, className, ...props }, ref) => {
    return /* @__PURE__ */ jsx(
      "span",
      {
        ref,
        className: cn(
          "badge",
          `badge-${variant}`,
          className
        ),
        ...props,
        children
      }
    );
  }
);
Badge.displayName = "Badge";
var sizeClasses2 = {
  sm: "max-w-md",
  md: "max-w-2xl",
  lg: "max-w-4xl",
  xl: "max-w-6xl",
  full: "max-w-[95vw]"
};
var Modal = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = "lg",
  closeOnBackdropClick = false
}) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);
  if (!isOpen) return null;
  return createPortal(
    /* @__PURE__ */ jsx(
      "div",
      {
        className: "fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60",
        onClick: closeOnBackdropClick ? onClose : void 0,
        children: /* @__PURE__ */ jsxs(
          "div",
          {
            className: cn(
              "bg-tg-bg rounded-2xl w-full max-h-[85vh] flex flex-col shadow-2xl animate-slide-up",
              sizeClasses2[size]
            ),
            onClick: (e) => e.stopPropagation(),
            children: [
              /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between px-6 py-4 border-b border-tg-border bg-tg-bg sticky top-0 z-10 rounded-t-2xl", children: [
                /* @__PURE__ */ jsx("h2", { className: "text-2xl font-bold text-tg-text", children: title }),
                /* @__PURE__ */ jsx(
                  "button",
                  {
                    onClick: onClose,
                    className: "p-2 text-tg-hint hover:text-tg-text hover:bg-tg-bg-secondary rounded-lg transition-colors",
                    "aria-label": "Close modal",
                    children: /* @__PURE__ */ jsxs(
                      "svg",
                      {
                        xmlns: "http://www.w3.org/2000/svg",
                        width: "20",
                        height: "20",
                        viewBox: "0 0 24 24",
                        fill: "none",
                        stroke: "currentColor",
                        strokeWidth: "2",
                        strokeLinecap: "round",
                        strokeLinejoin: "round",
                        children: [
                          /* @__PURE__ */ jsx("line", { x1: "18", y1: "6", x2: "6", y2: "18" }),
                          /* @__PURE__ */ jsx("line", { x1: "6", y1: "6", x2: "18", y2: "18" })
                        ]
                      }
                    )
                  }
                )
              ] }),
              /* @__PURE__ */ jsx("div", { className: "flex-1 overflow-y-auto px-6 py-6", children }),
              footer !== void 0 && /* @__PURE__ */ jsx("div", { className: "px-6 py-4 border-t border-tg-border bg-tg-bg sticky bottom-0 z-10 rounded-b-2xl", children: footer })
            ]
          }
        )
      }
    ),
    document.body
  );
};
var NotificationModal = ({
  isOpen,
  onClose,
  title,
  message,
  type = "info",
  buttonText = "OK"
}) => {
  if (!isOpen) return null;
  const config = {
    success: {
      icon: /* @__PURE__ */ jsxs("svg", { width: "48", height: "48", viewBox: "0 0 24 24", fill: "none", children: [
        /* @__PURE__ */ jsx("circle", { cx: "12", cy: "12", r: "10", stroke: "#10b981", strokeWidth: "2", fill: "#10b98120" }),
        /* @__PURE__ */ jsx("path", { d: "M8 12.5l2.5 2.5L16 9", stroke: "#10b981", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" })
      ] }),
      bgColor: "bg-green-50",
      borderColor: "border-green-200"
    },
    error: {
      icon: /* @__PURE__ */ jsxs("svg", { width: "48", height: "48", viewBox: "0 0 24 24", fill: "none", children: [
        /* @__PURE__ */ jsx("circle", { cx: "12", cy: "12", r: "10", stroke: "#ef4444", strokeWidth: "2", fill: "#ef444420" }),
        /* @__PURE__ */ jsx("path", { d: "M15 9l-6 6M9 9l6 6", stroke: "#ef4444", strokeWidth: "2", strokeLinecap: "round" })
      ] }),
      bgColor: "bg-red-50",
      borderColor: "border-red-200"
    },
    warning: {
      icon: /* @__PURE__ */ jsxs("svg", { width: "48", height: "48", viewBox: "0 0 24 24", fill: "none", children: [
        /* @__PURE__ */ jsx("path", { d: "M12 2L2 20h20L12 2z", stroke: "#f59e0b", strokeWidth: "2", fill: "#f59e0b20" }),
        /* @__PURE__ */ jsx("path", { d: "M12 9v4M12 17h.01", stroke: "#f59e0b", strokeWidth: "2", strokeLinecap: "round" })
      ] }),
      bgColor: "bg-amber-50",
      borderColor: "border-amber-200"
    },
    info: {
      icon: /* @__PURE__ */ jsxs("svg", { width: "48", height: "48", viewBox: "0 0 24 24", fill: "none", children: [
        /* @__PURE__ */ jsx("circle", { cx: "12", cy: "12", r: "10", stroke: "#3b82f6", strokeWidth: "2", fill: "#3b82f620" }),
        /* @__PURE__ */ jsx("path", { d: "M12 16v-4M12 8h.01", stroke: "#3b82f6", strokeWidth: "2", strokeLinecap: "round" })
      ] }),
      bgColor: "bg-blue-50",
      borderColor: "border-blue-200"
    }
  };
  const { icon, bgColor, borderColor } = config[type];
  return createPortal(
    /* @__PURE__ */ jsx(
      "div",
      {
        className: "fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm",
        onClick: onClose,
        children: /* @__PURE__ */ jsx(
          "div",
          {
            className: cn(
              "bg-white rounded-2xl w-full max-w-md shadow-2xl animate-slide-up",
              "transform transition-all"
            ),
            onClick: (e) => e.stopPropagation(),
            children: /* @__PURE__ */ jsxs("div", { className: "p-6 text-center", children: [
              /* @__PURE__ */ jsx("div", { className: "flex justify-center mb-4", children: icon }),
              /* @__PURE__ */ jsx("h3", { className: "text-xl font-bold text-gray-900 mb-2", children: title }),
              /* @__PURE__ */ jsx("p", { className: "text-gray-600 text-sm leading-relaxed whitespace-pre-line mb-6", children: message }),
              /* @__PURE__ */ jsx(
                Button,
                {
                  variant: "primary",
                  fullWidth: true,
                  onClick: onClose,
                  children: buttonText
                }
              )
            ] })
          }
        )
      }
    ),
    document.body
  );
};
var ConfirmDialog = ({
  title,
  message,
  confirmLabel = "X\xE1c nh\u1EADn",
  cancelLabel = "H\u1EE7y",
  onConfirm,
  onClose,
  variant = "info",
  isOpen
}) => {
  const buttonVariant = variant === "danger" ? "danger" : "primary";
  return /* @__PURE__ */ jsx(Modal, { isOpen, onClose, title, size: "sm", children: /* @__PURE__ */ jsxs("div", { className: "p-6", children: [
    /* @__PURE__ */ jsx("p", { className: "text-tg-text mb-6 whitespace-pre-line", children: message }),
    /* @__PURE__ */ jsxs("div", { className: "flex gap-3 justify-end", children: [
      /* @__PURE__ */ jsx(
        Button,
        {
          variant: "ghost",
          onClick: onClose,
          children: cancelLabel
        }
      ),
      /* @__PURE__ */ jsx(
        Button,
        {
          variant: buttonVariant,
          onClick: () => {
            onConfirm();
            onClose();
          },
          children: confirmLabel
        }
      )
    ] })
  ] }) });
};
var ChevronLeftIcon = () => /* @__PURE__ */ jsx("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: /* @__PURE__ */ jsx("path", { d: "M15 18l-6-6 6-6" }) });
var Header = ({
  title,
  subtitle,
  showBack = false,
  onBack,
  action,
  mobileMenu,
  className = "",
  actionLayout = "auto"
}) => {
  const hasActions = action || mobileMenu;
  return /* @__PURE__ */ jsxs("header", { className: `
      sticky top-0 z-10
      bg-tg-bg border-b border-tg-border
      ${className}
    `.trim().replace(/\s+/g, " "), children: [
    /* @__PURE__ */ jsxs("div", { className: `
        ${actionLayout === "auto" ? "hidden sm:flex" : actionLayout === "stack" ? "hidden" : "flex"}
        items-center justify-between px-4 py-3 gap-4
      `, children: [
      /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-3 flex-1 min-w-0", children: [
        showBack && /* @__PURE__ */ jsx(
          "button",
          {
            onClick: onBack,
            className: "flex-shrink-0 p-2 -ml-2 text-tg-primary hover:bg-tg-bg-secondary rounded-lg transition-colors",
            "aria-label": "Go back",
            children: /* @__PURE__ */ jsx(ChevronLeftIcon, {})
          }
        ),
        /* @__PURE__ */ jsxs("div", { className: "min-w-0 flex-1", children: [
          typeof title === "string" ? /* @__PURE__ */ jsx("h1", { className: "text-xl font-bold text-tg-text truncate", children: title }) : /* @__PURE__ */ jsx("div", { className: "text-xl font-bold text-tg-text", children: title }),
          subtitle && /* @__PURE__ */ jsx("p", { className: "text-sm text-tg-hint mt-0.5 truncate", children: subtitle })
        ] })
      ] }),
      hasActions && /* @__PURE__ */ jsx("div", { className: "flex items-center gap-2 flex-shrink-0 flex-wrap justify-end max-w-[50%]", children: action })
    ] }),
    actionLayout === "auto" && /* @__PURE__ */ jsxs("div", { className: "sm:hidden px-4 py-3", children: [
      /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between mb-2", children: [
        /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 flex-1 min-w-0", children: [
          showBack && /* @__PURE__ */ jsx(
            "button",
            {
              onClick: onBack,
              className: "flex-shrink-0 p-2 -ml-2 text-tg-primary hover:bg-tg-bg-secondary rounded-lg transition-colors",
              "aria-label": "Go back",
              children: /* @__PURE__ */ jsx(ChevronLeftIcon, {})
            }
          ),
          typeof title === "string" ? /* @__PURE__ */ jsx("h1", { className: "text-lg font-bold text-tg-text truncate", children: title }) : /* @__PURE__ */ jsx("div", { className: "text-lg font-bold text-tg-text", children: title })
        ] }),
        mobileMenu && /* @__PURE__ */ jsx("div", { className: "flex-shrink-0 ml-2", children: mobileMenu })
      ] }),
      subtitle && /* @__PURE__ */ jsx("p", { className: "text-sm text-tg-hint truncate mb-3", children: subtitle }),
      action && /* @__PURE__ */ jsx("div", { className: "flex gap-2 flex-wrap", children: action })
    ] }),
    actionLayout === "stack" && /* @__PURE__ */ jsxs("div", { className: "px-4 py-3", children: [
      /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 mb-3", children: [
        showBack && /* @__PURE__ */ jsx(
          "button",
          {
            onClick: onBack,
            className: "flex-shrink-0 p-2 -ml-2 text-tg-primary hover:bg-tg-bg-secondary rounded-lg transition-colors",
            "aria-label": "Go back",
            children: /* @__PURE__ */ jsx(ChevronLeftIcon, {})
          }
        ),
        /* @__PURE__ */ jsxs("div", { className: "flex-1 min-w-0", children: [
          typeof title === "string" ? /* @__PURE__ */ jsx("h1", { className: "text-xl font-bold text-tg-text truncate", children: title }) : /* @__PURE__ */ jsx("div", { className: "text-xl font-bold text-tg-text", children: title }),
          subtitle && /* @__PURE__ */ jsx("p", { className: "text-sm text-tg-hint mt-0.5 truncate", children: subtitle })
        ] }),
        mobileMenu
      ] }),
      hasActions && /* @__PURE__ */ jsx("div", { className: "flex gap-2 flex-wrap", children: action })
    ] })
  ] });
};
var PageHeader = ({
  title,
  subtitle,
  action,
  className = ""
}) => {
  return /* @__PURE__ */ jsx("div", { className: `
      sticky top-0 z-10
      bg-tg-bg border-b border-tg-border
      ${className}
    `.trim().replace(/\s+/g, " "), children: /* @__PURE__ */ jsx("div", { className: "px-4 py-3", children: /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between", children: [
    /* @__PURE__ */ jsxs("div", { className: "flex-1 min-w-0", children: [
      /* @__PURE__ */ jsx("h1", { className: "text-xl font-bold text-tg-text truncate", children: title }),
      subtitle && /* @__PURE__ */ jsx("p", { className: "text-sm text-tg-hint mt-0.5 truncate", children: subtitle })
    ] }),
    action && /* @__PURE__ */ jsx("div", { className: "flex-shrink-0 ml-4", children: action })
  ] }) }) });
};
var DateTimePicker = React.forwardRef(
  ({ label, value, onChange, type = "datetime-local", required, error, helperText, min, max, className }, ref) => {
    const CalendarIcon = /* @__PURE__ */ jsxs("svg", { width: "18", height: "18", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: [
      /* @__PURE__ */ jsx("rect", { x: "3", y: "4", width: "18", height: "18", rx: "2", stroke: "currentColor", strokeWidth: "2" }),
      /* @__PURE__ */ jsx("line", { x1: "16", y1: "2", x2: "16", y2: "6", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round" }),
      /* @__PURE__ */ jsx("line", { x1: "8", y1: "2", x2: "8", y2: "6", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round" }),
      /* @__PURE__ */ jsx("line", { x1: "3", y1: "10", x2: "21", y2: "10", stroke: "currentColor", strokeWidth: "2" })
    ] });
    const ClockIcon = /* @__PURE__ */ jsxs("svg", { width: "18", height: "18", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: [
      /* @__PURE__ */ jsx("circle", { cx: "12", cy: "12", r: "10", stroke: "currentColor", strokeWidth: "2" }),
      /* @__PURE__ */ jsx("path", { d: "M12 6V12L16 14", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round" })
    ] });
    const icon = type === "time" ? ClockIcon : CalendarIcon;
    return /* @__PURE__ */ jsxs("div", { className: "w-full", children: [
      label && /* @__PURE__ */ jsxs("label", { className: "block text-sm font-medium text-tg-text mb-1.5", children: [
        label,
        required && /* @__PURE__ */ jsx("span", { className: "text-tg-error ml-1", children: "*" })
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "relative", children: [
        /* @__PURE__ */ jsx("div", { className: "absolute left-3 top-1/2 -translate-y-1/2 text-tg-hint pointer-events-none", children: icon }),
        /* @__PURE__ */ jsx(
          "input",
          {
            ref,
            type,
            value,
            onChange: (e) => onChange(e.target.value),
            min,
            max,
            required,
            className: cn(
              "input pl-10",
              error && "error",
              // Custom styles for date/time inputs
              "[&::-webkit-calendar-picker-indicator]:cursor-pointer",
              "[&::-webkit-calendar-picker-indicator]:opacity-60",
              "[&::-webkit-calendar-picker-indicator]:hover:opacity-100",
              className
            )
          }
        )
      ] }),
      error && /* @__PURE__ */ jsx("p", { className: "mt-1 text-xs text-tg-error", children: error }),
      helperText && !error && /* @__PURE__ */ jsx("p", { className: "mt-1 text-xs text-tg-hint", children: helperText })
    ] });
  }
);
DateTimePicker.displayName = "DateTimePicker";
var FormField = ({
  label,
  required,
  error,
  helperText,
  children,
  className
}) => {
  return /* @__PURE__ */ jsxs("div", { className: cn("space-y-1.5", className), children: [
    label && /* @__PURE__ */ jsxs("label", { className: "block text-sm font-medium text-tg-text", children: [
      label,
      required && /* @__PURE__ */ jsx("span", { className: "text-tg-error ml-1", children: "*" })
    ] }),
    children,
    error && /* @__PURE__ */ jsx("p", { className: "text-xs text-tg-error mt-1", children: error }),
    helperText && !error && /* @__PURE__ */ jsx("p", { className: "text-xs text-tg-hint mt-1", children: helperText })
  ] });
};
var HelpTooltip = ({
  content,
  className,
  size = 16,
  side = "top",
  delayDuration = 200
}) => {
  return /* @__PURE__ */ jsx(TooltipPrimitive.Provider, { delayDuration, children: /* @__PURE__ */ jsxs(TooltipPrimitive.Root, { children: [
    /* @__PURE__ */ jsx(TooltipPrimitive.Trigger, { asChild: true, children: /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        className: cn(
          "inline-flex items-center justify-center",
          "text-tg-hint hover:text-tg-text",
          "transition-colors",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-tg-primary focus-visible:ring-offset-1 rounded-full",
          className
        ),
        "aria-label": "Help",
        children: /* @__PURE__ */ jsx(HelpCircle, { size })
      }
    ) }),
    /* @__PURE__ */ jsx(TooltipPrimitive.Portal, { children: /* @__PURE__ */ jsxs(
      TooltipPrimitive.Content,
      {
        side,
        sideOffset: 8,
        collisionPadding: 16,
        className: cn(
          // Layout & sizing
          "max-w-xs w-64 p-3",
          // Styling - Green background with white text
          "bg-tg-help border border-emerald-700 rounded-lg shadow-xl",
          "text-sm text-white leading-relaxed",
          // z-index to appear above modals (modals typically use z-40 to z-50)
          "z-[9999]",
          // Animation
          "animate-in fade-in-0 zoom-in-95",
          "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
          "data-[side=bottom]:slide-in-from-top-2",
          "data-[side=left]:slide-in-from-right-2",
          "data-[side=right]:slide-in-from-left-2",
          "data-[side=top]:slide-in-from-bottom-2"
        ),
        children: [
          typeof content === "string" ? /* @__PURE__ */ jsx("p", { children: content }) : content,
          /* @__PURE__ */ jsx(
            TooltipPrimitive.Arrow,
            {
              className: "fill-tg-section-separator",
              width: 12,
              height: 6
            }
          )
        ]
      }
    ) })
  ] }) });
};
var FormSection = ({
  title,
  subtitle,
  help,
  children,
  className
}) => {
  return /* @__PURE__ */ jsxs("div", { className: cn("space-y-4 mt-8 first:mt-0", className), children: [
    (title || subtitle) && /* @__PURE__ */ jsxs("div", { className: "space-y-1.5 pb-3 border-b border-tg-section-separator", children: [
      title && /* @__PURE__ */ jsxs("h3", { className: "flex items-center gap-2 text-lg font-semibold text-tg-text", children: [
        /* @__PURE__ */ jsx("span", { children: title }),
        help && /* @__PURE__ */ jsx(HelpTooltip, { content: help })
      ] }),
      subtitle && /* @__PURE__ */ jsx("p", { className: "text-sm text-tg-hint", children: subtitle })
    ] }),
    /* @__PURE__ */ jsx("div", { className: "space-y-4", children })
  ] });
};
var SectionTitle = ({
  children,
  className,
  help
}) => {
  return /* @__PURE__ */ jsxs("h4", { className: cn(
    "text-base font-semibold text-tg-text mt-6 mb-3 first:mt-0 flex items-center gap-2",
    className
  ), children: [
    /* @__PURE__ */ jsx("span", { children }),
    help && /* @__PURE__ */ jsx(HelpTooltip, { content: help, size: 14 })
  ] });
};
var PlusIcon = () => /* @__PURE__ */ jsxs("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: [
  /* @__PURE__ */ jsx("line", { x1: "12", y1: "5", x2: "12", y2: "19", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round" }),
  /* @__PURE__ */ jsx("line", { x1: "5", y1: "12", x2: "19", y2: "12", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round" })
] });
var XIcon = () => /* @__PURE__ */ jsxs("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: [
  /* @__PURE__ */ jsx("line", { x1: "18", y1: "6", x2: "6", y2: "18", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round" }),
  /* @__PURE__ */ jsx("line", { x1: "6", y1: "6", x2: "18", y2: "18", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round" })
] });
var FieldArray = ({
  label,
  help,
  addLabel = "+ Add",
  children,
  onAdd,
  className
}) => {
  return /* @__PURE__ */ jsxs("div", { className: cn("space-y-2", className), children: [
    label && /* @__PURE__ */ jsxs("label", { className: "flex items-center gap-2 text-sm font-medium text-tg-text", children: [
      /* @__PURE__ */ jsx("span", { children: label }),
      help && /* @__PURE__ */ jsx(HelpTooltip, { content: help })
    ] }),
    /* @__PURE__ */ jsx("div", { className: "space-y-2", children }),
    /* @__PURE__ */ jsx(
      Button,
      {
        type: "button",
        variant: "ghost",
        size: "xs",
        onClick: onAdd,
        icon: /* @__PURE__ */ jsx(PlusIcon, {}),
        className: "text-tg-primary hover:bg-tg-primary/10",
        children: addLabel
      }
    )
  ] });
};
var FieldArrayItem = ({
  children,
  onRemove,
  className
}) => {
  return /* @__PURE__ */ jsxs("div", { className: cn("flex items-start gap-2", className), children: [
    /* @__PURE__ */ jsx("div", { className: "flex-1", children }),
    /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        onClick: onRemove,
        className: "mt-1 p-2 text-tg-hint hover:text-tg-error hover:bg-tg-error/10 rounded-lg transition-colors",
        "aria-label": "Remove",
        children: /* @__PURE__ */ jsx(XIcon, {})
      }
    )
  ] });
};
var TelegramLoginWidget = ({
  botUsername,
  onAuth,
  buttonSize = "large",
  cornerRadius = 12,
  requestAccess = "write",
  lang = "en"
}) => {
  const containerRef = useRef(null);
  useEffect(() => {
    window.onTelegramAuth = (user) => {
      console.log("Telegram auth callback:", user);
      onAuth(user);
    };
    if (containerRef.current && !containerRef.current.querySelector("script")) {
      const script = document.createElement("script");
      script.src = "https://telegram.org/js/telegram-widget.js?22";
      script.setAttribute("data-telegram-login", botUsername);
      script.setAttribute("data-size", buttonSize);
      script.setAttribute("data-radius", cornerRadius.toString());
      script.setAttribute("data-onauth", "onTelegramAuth(user)");
      script.setAttribute("data-request-access", requestAccess);
      script.setAttribute("data-lang", lang);
      script.async = true;
      containerRef.current.appendChild(script);
    }
    return () => {
      delete window.onTelegramAuth;
    };
  }, [botUsername, buttonSize, cornerRadius, requestAccess, lang, onAuth]);
  return /* @__PURE__ */ jsx("div", { ref: containerRef, className: "flex justify-center" });
};
var List = ({ children, className = "" }) => {
  return /* @__PURE__ */ jsx("div", { className: `space-y-2 ${className}`, children });
};
var ListItem = ({
  children,
  className = "",
  onClick
}) => {
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: `p-4 bg-white border border-gray-200 rounded-lg hover:shadow-sm transition-shadow ${onClick ? "cursor-pointer hover:border-gray-300" : ""} ${className}`,
      onClick,
      children
    }
  );
};
var Tabs = ({
  tabs,
  activeTab,
  onChange,
  className = ""
}) => {
  return /* @__PURE__ */ jsx("div", { className: `flex gap-2 ${className}`, children: tabs.map((tab) => /* @__PURE__ */ jsxs(
    "button",
    {
      onClick: () => onChange(tab.key),
      className: `
            px-4 py-2 rounded-lg font-medium text-sm transition-all
            flex items-center gap-2
            ${activeTab === tab.key ? "bg-blue-600 text-white shadow-sm" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}
          `,
      children: [
        tab.icon,
        tab.label
      ]
    },
    tab.key
  )) });
};
var EmptyState = ({
  icon,
  title,
  description,
  action,
  className = ""
}) => {
  return /* @__PURE__ */ jsxs("div", { className: `flex flex-col items-center justify-center py-12 px-4 text-center ${className}`, children: [
    icon && /* @__PURE__ */ jsx("div", { className: "mb-4 text-gray-400", children: icon }),
    /* @__PURE__ */ jsx("h3", { className: "text-lg font-semibold text-gray-900 mb-2", children: title }),
    description && /* @__PURE__ */ jsx("p", { className: "text-sm text-gray-600 mb-6 max-w-md", children: description }),
    action && /* @__PURE__ */ jsx(
      "button",
      {
        onClick: action.onClick,
        className: "px-4 py-2 bg-blue-600 text-white rounded-lg font-medium text-sm hover:bg-blue-700 transition-colors",
        children: action.label
      }
    )
  ] });
};
var sizeClasses3 = {
  sm: "h-6 w-6 border-2",
  md: "h-12 w-12 border-2",
  lg: "h-16 w-16 border-4"
};
var LoadingSpinner = ({
  size = "md",
  className,
  text
}) => {
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-col items-center justify-center gap-4", children: [
    /* @__PURE__ */ jsx(
      "div",
      {
        className: cn(
          "animate-spin rounded-full border-tg-primary border-t-transparent",
          sizeClasses3[size],
          className
        )
      }
    ),
    text && /* @__PURE__ */ jsx("div", { className: "text-tg-hint", children: text })
  ] });
};

// src/assets/tadagram-logo.png
var tadagram_logo_default = "data:image/png;base64,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";

// src/assets/logo.ts
var getTadagramLogo = () => tadagram_logo_default;
var LoginPage = ({
  appName,
  appDescription = "Login with your Telegram account to continue",
  botUsername,
  onAuth,
  logoUrl,
  isWebEnvironment,
  errorMessage,
  children
}) => {
  const logo = logoUrl || getTadagramLogo();
  const [logoError, setLogoError] = React.useState(false);
  return /* @__PURE__ */ jsx("div", { className: "min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4", children: /* @__PURE__ */ jsxs("div", { className: "w-full max-w-md", children: [
    /* @__PURE__ */ jsxs("div", { className: "bg-white rounded-3xl shadow-2xl overflow-hidden", children: [
      /* @__PURE__ */ jsxs("div", { className: "bg-gradient-to-r from-blue-500 to-purple-600 px-8 py-10 text-center", children: [
        /* @__PURE__ */ jsx("div", { className: "mb-4 flex justify-center", children: !logoError ? /* @__PURE__ */ jsx(
          "img",
          {
            src: logo,
            alt: appName,
            className: "w-20 h-20 rounded-2xl shadow-lg object-contain bg-white p-2",
            onError: () => setLogoError(true)
          }
        ) : /* @__PURE__ */ jsx("div", { className: "w-20 h-20 rounded-2xl shadow-lg bg-white flex items-center justify-center", children: /* @__PURE__ */ jsx("div", { className: "text-4xl font-bold text-blue-500", children: appName.charAt(0) }) }) }),
        /* @__PURE__ */ jsx("h1", { className: "text-3xl font-bold text-white mb-2", children: appName }),
        /* @__PURE__ */ jsx("p", { className: "text-blue-100 text-sm", children: appDescription })
      ] }),
      /* @__PURE__ */ jsx("div", { className: "px-8 py-10", children: children ? children : isWebEnvironment ? /* @__PURE__ */ jsxs("div", { className: "space-y-6", children: [
        /* @__PURE__ */ jsxs("div", { className: "flex flex-col items-center", children: [
          /* @__PURE__ */ jsx(
            TelegramLoginWidget,
            {
              botUsername,
              onAuth,
              buttonSize: "large",
              cornerRadius: 12
            }
          ),
          /* @__PURE__ */ jsx("p", { className: "text-sm text-gray-500 text-center mt-4 max-w-xs", children: "Click the button above to login with your Telegram account" })
        ] }),
        /* @__PURE__ */ jsxs("div", { className: "relative", children: [
          /* @__PURE__ */ jsx("div", { className: "absolute inset-0 flex items-center", children: /* @__PURE__ */ jsx("div", { className: "w-full border-t border-gray-200" }) }),
          /* @__PURE__ */ jsx("div", { className: "relative flex justify-center text-sm", children: /* @__PURE__ */ jsx("span", { className: "px-4 bg-white text-gray-500", children: "Secure authentication via Telegram" }) })
        ] }),
        /* @__PURE__ */ jsxs("p", { className: "text-xs text-gray-400 text-center leading-relaxed", children: [
          "By logging in, you agree to our",
          " ",
          /* @__PURE__ */ jsx("a", { href: "#", className: "text-blue-500 hover:underline", children: "Terms of Service" }),
          " ",
          "and",
          " ",
          /* @__PURE__ */ jsx("a", { href: "#", className: "text-blue-500 hover:underline", children: "Privacy Policy" })
        ] })
      ] }) : /* @__PURE__ */ jsxs("div", { className: "text-center py-8", children: [
        /* @__PURE__ */ jsx("div", { className: "inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 mb-4", children: /* @__PURE__ */ jsx("svg", { className: "w-8 h-8 text-red-600", fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", children: /* @__PURE__ */ jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" }) }) }),
        /* @__PURE__ */ jsx("h3", { className: "text-lg font-semibold text-gray-900 mb-2", children: errorMessage || "Authentication Required" }),
        /* @__PURE__ */ jsx("p", { className: "text-sm text-gray-600", children: "Please open this app from Telegram Mini App" })
      ] }) }),
      /* @__PURE__ */ jsx("div", { className: "bg-gray-50 px-8 py-4 text-center border-t border-gray-100", children: /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-center space-x-2 text-xs text-gray-500", children: [
        /* @__PURE__ */ jsx("svg", { className: "w-4 h-4", fill: "currentColor", viewBox: "0 0 24 24", children: /* @__PURE__ */ jsx("path", { d: "M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221l-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.446 1.394c-.14.18-.357.295-.6.295-.002 0-.003 0-.005 0l.213-3.054 5.56-5.022c.24-.213-.054-.334-.373-.121l-6.869 4.326-2.96-.924c-.64-.203-.658-.64.135-.954l11.566-4.458c.538-.196 1.006.128.832.941z" }) }),
        /* @__PURE__ */ jsx("span", { children: "Powered by Telegram" })
      ] }) })
    ] }),
    /* @__PURE__ */ jsxs("p", { className: "text-center text-sm text-gray-500 mt-6", children: [
      "Need help? Contact",
      " ",
      /* @__PURE__ */ jsx("a", { href: "https://t.me/tadagram_support", className: "text-blue-500 hover:underline", children: "@tadagram_support" })
    ] })
  ] }) });
};
var variantColors = {
  default: { bg: "#f3f4f6", text: "#374151" },
  primary: { bg: "#dbeafe", text: "#1e40af" },
  success: { bg: "#d1fae5", text: "#065f46" },
  warning: { bg: "#fef3c7", text: "#92400e" },
  danger: { bg: "#fee2e2", text: "#991b1b" }
};
var Avatar = ({
  children,
  size = 40,
  variant = "default",
  style
}) => {
  const colors = variantColors[variant] || variantColors.default;
  return /* @__PURE__ */ jsx(
    "div",
    {
      style: {
        width: size,
        height: size,
        borderRadius: 8,
        background: colors.bg,
        color: colors.text,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 700,
        fontSize: size * 0.45,
        ...style
      },
      children
    }
  );
};
var SearchInput = ({
  value,
  onChange,
  placeholder = "Search...",
  onClear,
  icon,
  style
}) => {
  return /* @__PURE__ */ jsxs("div", { style: { display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", ...style }, children: [
    icon,
    /* @__PURE__ */ jsx(
      "input",
      {
        type: "text",
        placeholder,
        value,
        onChange: (e) => onChange(e.target.value),
        style: {
          flex: 1,
          padding: "6px 8px",
          borderRadius: 6,
          border: "1px solid #e5e7eb",
          outline: "none",
          fontSize: 14
        }
      }
    ),
    value && onClear && /* @__PURE__ */ jsx(
      "button",
      {
        onClick: onClear,
        "aria-label": "Clear search",
        style: {
          background: "transparent",
          border: "none",
          fontSize: 18,
          cursor: "pointer",
          color: "#6b7280",
          padding: 0,
          lineHeight: 1
        },
        children: "\xD7"
      }
    )
  ] });
};
var Sidebar = ({
  children,
  width = 280,
  style,
  className
}) => {
  return /* @__PURE__ */ jsx(
    "div",
    {
      className,
      style: {
        width,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRight: "1px solid #e5e7eb",
        backgroundColor: "#ffffff",
        overflow: "hidden",
        ...style
      },
      children
    }
  );
};
var SidebarHeader = ({ children, style }) => {
  return /* @__PURE__ */ jsx("div", { style: { padding: "12px 16px", borderBottom: "1px solid #f3f4f6", flexShrink: 0, ...style }, children });
};
var SidebarContent = ({ children, style }) => {
  return /* @__PURE__ */ jsx("div", { style: { flex: 1, overflowY: "auto", padding: "8px", ...style }, children });
};
var ListItem2 = ({
  onClick,
  active = false,
  disabled = false,
  avatar,
  title,
  subtitle,
  meta,
  badge,
  className,
  style
}) => {
  const [isHovered, setIsHovered] = React.useState(false);
  const baseStyle = {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "12px 16px",
    cursor: onClick && !disabled ? "pointer" : "default",
    backgroundColor: active ? "#f0f9ff" : isHovered && onClick && !disabled ? "#f9fafb" : "transparent",
    // Default: transparent
    transition: "background-color 0.15s ease",
    opacity: disabled ? 0.5 : 1,
    borderBottom: "1px solid #e5e7eb",
    // Divider between items
    ...style
  };
  return /* @__PURE__ */ jsxs(
    "div",
    {
      onClick: onClick && !disabled ? onClick : void 0,
      role: onClick ? "button" : void 0,
      tabIndex: onClick && !disabled ? 0 : void 0,
      className,
      style: baseStyle,
      onMouseEnter: () => setIsHovered(true),
      onMouseLeave: () => setIsHovered(false),
      onKeyDown: (e) => {
        if (onClick && !disabled && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onClick();
        }
      },
      children: [
        avatar && /* @__PURE__ */ jsxs("div", { style: { position: "relative", flexShrink: 0 }, children: [
          avatar,
          badge
        ] }),
        /* @__PURE__ */ jsx("div", { style: { flex: 1, minWidth: 0 }, children: /* @__PURE__ */ jsxs("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "flex-start" }, children: [
          /* @__PURE__ */ jsxs("div", { style: { overflow: "hidden", flex: 1, minWidth: 0 }, children: [
            /* @__PURE__ */ jsx("div", { style: { fontWeight: 600, whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }, children: title }),
            subtitle && /* @__PURE__ */ jsx("div", { style: { fontSize: 13, color: "#6b7280", marginTop: 2, whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }, children: subtitle })
          ] }),
          meta && /* @__PURE__ */ jsx("div", { style: { flexShrink: 0, marginLeft: 12 }, children: meta })
        ] }) })
      ]
    }
  );
};
var TITLE_COLOR_STYLES = {
  default: {
    text: "text-tg-text",
    bg: "hover:bg-tg-bg-secondary"
  },
  primary: {
    text: "text-tg-primary font-semibold",
    bg: "hover:bg-tg-primary/10"
  },
  success: {
    text: "text-tg-success font-semibold",
    bg: "hover:bg-tg-success/10"
  },
  warning: {
    text: "text-tg-warning font-semibold",
    bg: "hover:bg-tg-warning/10"
  },
  danger: {
    text: "text-tg-danger font-semibold",
    bg: "hover:bg-tg-danger/10"
  }
};
var CollapsibleSection = ({
  title,
  children,
  defaultOpen = false,
  className,
  titleColor = "default"
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const colorStyle = TITLE_COLOR_STYLES[titleColor];
  return /* @__PURE__ */ jsxs("div", { className: `rounded-lg border bg-white ${className || ""}`, children: [
    /* @__PURE__ */ jsxs(
      "button",
      {
        onClick: () => setOpen((v) => !v),
        className: `flex w-full items-center justify-between px-4 py-3 text-left text-sm font-medium transition-colors ${colorStyle.bg}`,
        children: [
          /* @__PURE__ */ jsx("p", { className: `text-[1rem] mb-0 capitalize ${colorStyle.text}`, children: title }),
          /* @__PURE__ */ jsx(
            "span",
            {
              className: `transition-transform duration-300 text-[28px] ${colorStyle.text} ${open ? "rotate-180" : ""}`,
              children: "\u25BE"
            }
          )
        ]
      }
    ),
    /* @__PURE__ */ jsx(
      "div",
      {
        className: `border-t overflow-hidden transition-all duration-300 ease-in-out ${open ? "max-h-[1000px] opacity-100" : "max-h-0 opacity-0"}`,
        children: /* @__PURE__ */ jsx("div", { className: "px-4 py-3 space-y-2", children })
      }
    )
  ] });
};

export { Avatar, Badge, Button, Card, CardBody, CardFooter, CardHeader, Checkbox, CollapsibleSection, ConfirmDialog, DateTimePicker, EmptyState, FieldArray, FieldArrayItem, FormField, FormSection, Header, HelpTooltip, Input, List, ListItem, LoadingSpinner, LoginPage, Modal, NotificationModal, PageHeader, SearchInput, SectionTitle, Select, Sidebar, SidebarContent, SidebarHeader, Tabs, ListItem2 as TelegramListItem, TelegramLoginWidget, TextArea, cn, getTadagramLogo };
//# sourceMappingURL=index.mjs.map
//# sourceMappingURL=index.mjs.map